import { Suspense, useMemo, useCallback, useState, useEffect } from 'react';
import { Await, useLoaderData, useLocation, Link, useParams } from 'react-router-dom';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import Filter from '../../components/filter/Filter';
import Map from '../../components/map/Map';
import { Helmet } from 'react-helmet-async';
import PaginatedCards from '../../components/paginatedCards/PaginatedCards';
import categoriesData from '../../components/category/CategoriesData';
import './categoryPage.scss';

const CategoryPage = () => {
  const data = useLoaderData();
  const location = useLocation();
  const { category } = useParams();
  const [filteredProducts, setFilteredProducts] = useState([]);
  const pathParts = location.pathname
    .split('/')
    .filter((part) => part !== 'category' && part !== '');
  const currentCategory = pathParts[0] || null;
  const searchParams = new URLSearchParams(location.search);
  const currentSubCategory = searchParams.get('subcategory')
    ? searchParams.get('subcategory').replace(/%20/g, ' ')
    : null;

  const getOptions = useCallback((categoriesData) => {
    const findOptions = categoriesData.flatMap((category) =>
      category.subcategories.filter(
        (subcategory) => subcategory.name === currentSubCategory
      )
    );

    if (findOptions.length > 0) {
      return findOptions;
    }
    return null;
  }, [categoriesData, currentSubCategory]);

  const options = getOptions(categoriesData);
  
  const formatCategory = (text) => {
    return text ? text.replace(/ /g, '_') : '_';
  };
  const formatSubCategory = (text) => {
    return text ? text.replace(/ /g, '+') : '+';
  };

  const filteredPosts = useMemo(() => {
    if (!data.postResponse) return [];
    return data.postResponse.data.filter((post) => {
      const isCategoryMatch = post.category.replace(/ /g, '_') === currentCategory;
      const isSubCategoryMatch =
        !currentSubCategory || post.subcategory === currentSubCategory;
      return isCategoryMatch && isSubCategoryMatch;
    });
  }, [data.postResponse, currentCategory, currentSubCategory]);


  return (
    <div className="categoryPage">
   <Helmet>
     <link
    rel="canonical"
    href={`https://www.naijads.ng/category/${category?.toLowerCase().replace(/_/g, '-')}`}
  />
  <title>
    {currentCategory
      ? currentSubCategory
        ? `${currentSubCategory} free ads in Nigeria – Naijads`
        : `${currentCategory} – Browse Listings | Naijads`
      : 'Browse categories | Naijads'}
  </title>

  <meta
    name="description"
    content={
      currentSubCategory
        ? `Explore ${currentSubCategory} under ${currentCategory} on Naijads. Post free ads or discover local listings.`
        : `Browse ${currentCategory} listings on Naijads. Find deals or post your ad for free.`
    }
  />

  {/* Open Graph */}
  <meta property="og:type" content="website" />
  <meta
    property="og:title"
    content={
      currentSubCategory
        ? `${currentSubCategory} free ads in Nigeria – Naijads.ng`
        : `${currentCategory} listings – Naijads.ng`
    }
  />
  <meta
    property="og:description"
    content={
      currentSubCategory
        ? `Explore ${currentSubCategory} classified ads under ${currentCategory}. Post your ad for free on Naijads.`
        : `Find great listings in ${currentCategory} on Naijads. Totally free to post and browse.`
    }
  />
  <meta
    property="og:url"
    content={`https://www.naijads.ng${location.pathname}${location.search}`}
  />
  <meta
    property="og:image"
    content="https://www.naijads.ng/Tayo.svg"
  />
  <meta property="og:site_name" content="Naijads" />

  {/* Twitter Card */}
  <meta name="twitter:card" content="summary_large_image" />
  <meta
    name="twitter:title"
    content={
      currentSubCategory
        ? `${currentSubCategory} free ads in Nigeria – Naijads`
        : `${currentCategory} listings – Naijads`
    }
  />
  <meta
    name="twitter:description"
    content={
      currentSubCategory
        ? `Browse ${currentSubCategory} listings under ${currentCategory} on Naijads.ng`
        : `Explore ${currentCategory} classified ads in Nigeria.`
    }
  />
  <meta
    name="twitter:image"
    content="https://www.naijads.ng/Tayo.svg"
  />
  {/* Structured Data: schema.org CollectionPage */}
  <script
    type="application/ld+json"
    dangerouslySetInnerHTML={{
      __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": currentSubCategory
          ? `${currentSubCategory} – Naijads`
          : `${currentCategory} – Naijads`,
        "url": `https://www.naijads.ng${location.pathname}${location.search}`,
        "description": currentSubCategory
          ? `Explore ${currentSubCategory} ads in ${currentCategory} on Naijads.ng`
          : `Browse ${currentCategory} listings on Naijads – free classified ads in Nigeria`
      })
    }}
  />
</Helmet>
      <div className="categoryContainer">
        <div className="categoryWrapper">
          <div className="breadCrumbs">
          <p>
          {currentSubCategory ? (
      <Link to={`/category/${formatCategory(currentCategory)}`}>
        <span>{currentCategory.replaceAll('_', ' ')}</span>
      </Link>
    ) : (
      currentCategory ? currentCategory.replaceAll('_', ' ') : 'Chose category'
    )}

    {currentSubCategory && (
      <> / <Link to={`/category/${formatCategory(currentCategory)}?subcategory=${formatSubCategory(currentSubCategory)}`}>
        <span>{currentSubCategory.replaceAll('_', ' ')}</span>
      </Link></>
    )}
  </p>
</div>

          <Filter
            selectedCategory={currentCategory}
            selectedSubCategory={options}
            subCategoryData={categoriesData}
            setFilteredProducts={setFilteredProducts}
          />
          <Suspense fallback={<p>Loading...</p>}>
           <Await
  resolve={data.postResponse}
  errorElement={<p>Error loading ads!</p>}
>
  {(postResponse) => {
    if (filteredProducts.length === 0) {
      return (
        <DotLottieReact
          src="/animations/Sold-out.lottie"
          loop={false}
          autoplay
        />
      );
    }
    return <PaginatedCards posts={filteredProducts} />;
  }}
</Await>
          </Suspense>
        </div>
      </div>
   <div className="categoryMapContainer">
 <Suspense fallback={<p>Loading...</p>}>
          <Await
            resolve={data.postResponse}
            errorElement={<p>Error loading ads!</p>}
          >
            {(postResponse) => {
              return filteredProducts.length > 0 ? (
                <Map items={filteredProducts} />
              ) : (
                <p></p>
              );
            }}
          </Await>
        </Suspense>   
      </div>

    </div>
  );
};

export default CategoryPage;
